import { useCallback, useEffect, useState } from 'react';

export type AppInstallPlatform = 'ios' | 'android' | 'desktop';

/** Chromium's install event is not in lib.dom yet. */
type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

export function detectAppInstallPlatform(
  userAgent: string,
  maxTouchPoints = 0,
): AppInstallPlatform {
  const ua = userAgent.toLowerCase();
  if (/iphone|ipod|ipad/.test(ua)) {
    return 'ios';
  }
  // iPadOS 13+ reports a desktop Safari UA; touch points give it away.
  if (/macintosh/.test(ua) && maxTouchPoints > 1) {
    return 'ios';
  }
  if (/android/.test(ua)) {
    return 'android';
  }
  return 'desktop';
}

/** True once the page runs from an installed home-screen icon. */
function detectStandaloneDisplay(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  if (window.matchMedia?.('(display-mode: standalone)').matches) {
    return true;
  }
  return (window.navigator as { standalone?: boolean }).standalone === true;
}

/**
 * Home-screen install state for the "Get the app" sheet: which platform's manual
 * steps to show, and whether the browser offers a one-tap install prompt.
 */
export function useAppInstallPrompt() {
  const [platform, setPlatform] = useState<AppInstallPlatform>('desktop');
  const [installed, setInstalled] = useState(false);
  const [promptEvent, setPromptEvent] = useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    setPlatform(detectAppInstallPlatform(window.navigator.userAgent, window.navigator.maxTouchPoints));
    setInstalled(detectStandaloneDisplay());

    const onBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setPromptEvent(event as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setInstalled(true);
      setPromptEvent(null);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!promptEvent) {
      return false;
    }
    await promptEvent.prompt();
    const choice = await promptEvent.userChoice;
    setPromptEvent(null);
    return choice.outcome === 'accepted';
  }, [promptEvent]);

  return {
    platform,
    installed,
    canPromptInstall: promptEvent !== null,
    promptInstall,
  };
}
